// Goal priority ordering and re-numbering.
//
// A rank is a position in a list, not a label stuck to a goal: the ranked goals
// have to read 1..N with no holes. Anything below the cutoff carries rank 0 and
// sorts after the ranked ones.
import { TOP_PRIORITY_COUNT } from '../constants/goals';

/**
 * Ranked goals (1..N) first in ascending order; unranked ones (rank 0) after,
 * keeping their existing relative order.
 */
export const sortByPriority = (a, b) => {
	const ra = a.priority_rank ?? 0;
	const rb = b.priority_rank ?? 0;
	if (ra === 0 && rb === 0) return 0;
	if (ra === 0) return 1;
	if (rb === 0) return -1;
	return ra - rb;
};

/**
 * Close any hole in the 1..N run and promote whatever now falls inside the
 * cutoff. Removing the goal ranked 2 of 5 used to leave 1, 3, 4, 5 on the cards
 * and left the sixth goal unranked even though a slot had just opened up.
 *
 * A list that was never ranked is returned untouched: someone who has not set
 * priorities should not acquire them by deleting a goal.
 */
export const resequenceRanks = (goals) => {
	if (!goals.some((goal) => (goal.priority_rank ?? 0) > 0)) return goals;
	return [...goals].sort(sortByPriority).map((goal, index) => ({
		...goal,
		priority_rank: index < TOP_PRIORITY_COUNT ? index + 1 : 0,
	}));
};

/**
 * Only the ranks that actually moved, in the shape the priority-ranks endpoint
 * takes. An empty result means there is no request worth sending.
 */
export const changedRanks = (before, after) => {
	const previous = new Map(
		before.map((goal) => [goal.goal_setting_id, goal.priority_rank ?? 0])
	);
	return after
		.filter(
			(goal) =>
				goal.goal_setting_id != null &&
				previous.get(goal.goal_setting_id) !== goal.priority_rank
		)
		.map((goal) => ({
			goal_setting_id: goal.goal_setting_id,
			priority_rank: goal.priority_rank,
		}));
};
